import { SceneGraphNode } from '../../engine/models/scene-graph.model';
import { isEqual, partition } from 'lodash';
import { mergeSceneGraphNodes } from './merge-scene-graph-nodes';

function mergeAnonymousGraphs(original: SceneGraphNode[], additions: SceneGraphNode[]) {
  // i know that original and additions are collections of anonymous nodes
  // for each addition,
  // is the addition equal to an original node?
  // if it is not, append it

  return additions.reduce((acc, addition) => {
    for (const node of original) {
      if (!isEqual(node, addition)) {
        acc.push(addition);
      }
    }
    return acc;
  }, original);
}

function mergeNonAnonymousGraphs(original: SceneGraphNode[], additions: SceneGraphNode[]) {
  const originalSceneGraphsDict: {
    [rootId: string]: SceneGraphNode;
  } = original.reduce((acc, root) => {
    acc[root.objectId] = root;
    return acc;
  }, {});

  const newSceneGraphsDict = additions.reduce(
    (acc, root) => {
      // if a scene graph id doesn't exist, include it
      if (acc.nodes[root.objectId] === undefined) {
        acc.nodes[root.objectId] = root;
        return acc;
      }

      // if a scene graph exists, and its not the same, merge it with the original
      if (!isEqual(root, acc.nodes[root.objectId])) {
        const merged = mergeSceneGraphNodes(acc.nodes[root.objectId], root);

        acc.nodes[root.objectId] = merged.root[0];
        acc.graphsMerged.push(root.objectId);
        acc.objectIdsUniqueToNew[root.objectId] = merged.objectIdsUniqueToNew;
        acc.objectIdsUniqueToOriginal[root.objectId] = merged.objectIdsUniqueToOriginal;
      }

      // if a scene graph exists, and its the same
      // do nothing

      return acc;
    },
    {
      nodes: originalSceneGraphsDict,
      graphsMerged: [] as string[],
      objectIdsUniqueToNew: {} as { [rootId: string]: { [objectId: string]: true } },
      objectIdsUniqueToOriginal: {} as { [rootId: string]: { [objectId: string]: true } },
    },
  );

  // convert from dict back to array
  const newSceneGraph = Object.keys(newSceneGraphsDict.nodes).reduce((acc, rootId) => {
    acc.push(newSceneGraphsDict.nodes[rootId]);
    return acc;
  }, [] as SceneGraphNode[]);

  return {
    newSceneGraph,
    graphsMerged: newSceneGraphsDict.graphsMerged,
    objectIdsUniqueToNew: newSceneGraphsDict.objectIdsUniqueToNew,
    objectIdsUniqueToOriginal: newSceneGraphsDict.objectIdsUniqueToOriginal,
  };
}

export function mergeSceneGraphs(originalGraph: SceneGraphNode[], additions: SceneGraphNode[]) {
  // need to handle anonymous objects.
  // anonymous objects need to be combined separately, and merged with the rest

  const partitioner = (root) => root.objectId !== undefined;

  // handle anonymous nodes
  const [originalNodesWithId, originalAnonymousNodes] = partition(originalGraph, partitioner);

  const [additionsWithId, additionsAnonymous] = partition(additions, partitioner);

  // take additions with id and push them into originals with ids
  const combinedGraphsWithNoIds = mergeAnonymousGraphs(originalAnonymousNodes, additionsAnonymous);

  const { newSceneGraph, graphsMerged, objectIdsUniqueToNew, objectIdsUniqueToOriginal } =
    mergeNonAnonymousGraphs(originalNodesWithId, additionsWithId);

  return {
    sceneGraph: [...combinedGraphsWithNoIds, ...newSceneGraph],
    graphsMerged,
    objectIdsUniqueToNew,
    objectIdsUniqueToOriginal,
  };
}

function makeUpdateDict(
  graphsMerged: string[],
  uniqueObjectIds: { [rootId: string]: { [objectId: string]: true } },
) {
  const updateDict: { [objectId: string]: false } = {};
  for (const rootId of graphsMerged) {
    const objectIds = Object.keys(uniqueObjectIds[rootId]);
    for (const objectId of objectIds) {
      updateDict[objectId] = false;
    }
  }
  return updateDict;
}

// since we merged scene graphs, we must update show, select, and pickThroughObjects
// objects that are new additions from another model's scene graph must be explicity
// set to false in show, select, pickthrough objects.  i.e. they are not a part of this scene.
// preserve the original contents of show, select, pickthrough objects
// an updateDict is all the objectIds to set to false.

export function updateChapterShowSelectPickThroughObjects(
  chapters,
  graphsMerged: string[],
  uniqueObjectIds,
) {
  const updateDict = makeUpdateDict(graphsMerged, uniqueObjectIds);
  return chapters.map((chapter) => {
    return {
      ...chapter,
      showObjects: { ...chapter.showObjects, ...updateDict },
      selectObjects: { ...chapter.selectObjects, ...updateDict },
      pickThroughObjects: { ...chapter.pickThroughObjects, ...updateDict },
    };
  });
}
